/**
 * assembleLaunchInputs — build the outbound `inputs` object for one launch
 * (or retry), on BOTH the reshaped and the flat launch contract.
 *
 * Extracted verbatim from `InstanceDetailDrawer.handleLaunch`,
 * `ui/apps/outlook-addin/src/components/InstanceDetailDrawer.tsx` 1515–1563
 * (`handleRetry`, 1788–1821, calls the identical two branches — this module
 * serves both call sites, which is what keeps a retry from emitting a
 * different shape than the launch it retries).
 *
 * RESHAPED branch — `binding.isReshaped`: blank-only backfill of the
 * declared term-identity quartet onto `actions[0]` (`withTermIdentity`,
 * copied below), then the ONE shared assembler `buildLaunchPayload`, which
 * filters both slices against the schema's own declared keys.
 *
 * FLAT branch — today's exact spread, unchanged for the whole live fleet
 * (`mortgagee_update_v1_2` included):
 *
 *   { ...next, effective_date-fallback?, policy_effective_date?,
 *     policy_expiration_date?, ...injected }
 *
 * `...injected` is spread LAST — this is load-bearing, not incidental. It is
 * how a launcher-owned value (e.g. `client_name` resolved from the DB) wins
 * over whatever the form's `next` happened to carry for the same key, and it
 * is one of the two precedence facts Phase 1's characterization tests pin
 * (`InstanceDetailDrawer.launchAction.test.tsx` / `.policyGate.test.tsx`).
 * Reordering this spread is a silent behaviour change, not a refactor.
 *
 * `chosenTerm` is NOT recomputed here — it is the caller's own
 * `resolveLaunchTerm(...).chosenTerm`, passed in. This function only
 * consumes it (for the blank-date fallback and the term-identity backfill);
 * it never re-derives it, so a caller cannot accidentally launch a
 * different term than the one the guards just validated against.
 *
 * Pure: no React, no Office.js, no network.
 */
import type { PolicyInForce } from "~/common/workflows/types/client";
import {
  buildLaunchPayload,
  type BuiltLaunchPayload,
} from "~/common/workflows/launch/buildLaunchPayload";
import {
  mergeLaunchSlice,
  splitLaunchValues,
  type LaunchBinding,
} from "~/common/workflows/launch/launchSchemaBinding";
import { termIdentityWrites } from "~/common/workflows/launch/PolicyTermField";

/**
 * Backfill the DECLARED term-identity keys onto `actions[0]` from the
 * resolved term row — BLANK keys only.
 *
 * COPIED (not re-exported) from `InstanceDetailDrawer.tsx` 478–502 — that
 * function is currently add-in-local and not exported from any shared
 * module. Phase 3 deletes the add-in's copy and switches `handleLaunch` /
 * `handleRetry` to call `assembleLaunchInputs` instead, at which point this
 * becomes the only copy.
 *
 * Exists for the two paths that never render a term control — AUTO-LAUNCH
 * (whose action is whatever `prefill` emitted) and a RETRY of such a launch.
 * Left out, those paths would ship an action carrying a bare policy number,
 * which both terms of a policy share: the substrate falls back to the term
 * in force TODAY and its matcher tries exact-term equality BEFORE window
 * containment, so a future-term change lands on the current term (instances
 * 3b35db7f / e036c04d, 2026-08-05).
 */
function withTermIdentity(
  termIdentityKeys: readonly string[],
  actions: Record<string, unknown>[],
  term: PolicyInForce | null,
): Record<string, unknown>[] {
  // NEVER invents an action: an empty list stays empty, exactly as
  // `splitLaunchValues` returned it.
  if (actions.length === 0 || !term || termIdentityKeys.length === 0) {
    return actions;
  }
  const base: Record<string, unknown> = { ...actions[0] };
  for (const [key, value] of Object.entries(
    termIdentityWrites(termIdentityKeys, term),
  )) {
    const current = base[key];
    const blank =
      current === undefined ||
      current === null ||
      (typeof current === "string" && current.trim() === "");
    if (!blank) continue;
    if (value === undefined || value === null || value === "") continue;
    base[key] = value;
  }
  return [base, ...actions.slice(1)];
}

export interface AssembleLaunchInputsArgs {
  binding: LaunchBinding;
  /** The ONE values record the caller was handed (`handleLaunch(next)`) or
   *  holds in state (`handleRetry`'s `values`). */
  next: Record<string, unknown>;
  /** Run-level values only the LAUNCHER knows — `idempotency_key`, identity
   *  fields, `client_sk` / `client_name`, `oversight_level`, `use_sp_login`.
   *  Merged over the form's run level and, on the reshaped contract, filtered
   *  by the SAME declared-key allow-list as everything else (an injected key
   *  the schema does not declare is dropped and reported, never smuggled
   *  through). Host-chosen — this module never decides what rides here. */
  injected?: Record<string, unknown>;
  /** `resolveLaunchTerm(...).chosenTerm` — the term the guards already
   *  validated against. */
  chosenTerm: PolicyInForce | null;
}

export type AssembledLaunchInputs = Pick<BuiltLaunchPayload, "inputs" | "dropped">;

export function assembleLaunchInputs({
  binding,
  next,
  injected,
  chosenTerm,
}: AssembleLaunchInputsArgs): AssembledLaunchInputs {
  const { runLevelValues, actions } = splitLaunchValues(binding, next);

  if (binding.isReshaped) {
    const built = buildLaunchPayload({
      binding,
      runLevelValues,
      // Blank-only backfill for the auto-launch / retry-of-auto-launch paths
      // that never rendered a term control.
      actions: withTermIdentity(binding.roles.termIdentity, actions, chosenTerm),
      injected,
    });
    return { inputs: built.inputs, dropped: built.dropped };
  }

  // FLAT CONTRACT. `flat` here is the merged read view of the SAME `next` —
  // for an un-reshaped binding `splitLaunchValues` yields no actions, so
  // `flat.effective_date` and `next.effective_date` are the same value; this
  // mirrors `handleLaunch`'s own `flat.effective_date` read (1419–1428)
  // without requiring the caller to pass `flat` separately.
  const flat = mergeLaunchSlice(binding, runLevelValues, actions[0]);
  const typedEffectiveDate =
    typeof flat.effective_date === "string" ? flat.effective_date.trim() : "";
  const effectiveDateForLaunch =
    typedEffectiveDate
    || chosenTerm?.effective_date
    || (typeof flat.effective_date === "string" ? flat.effective_date : null);

  const inputs: Record<string, unknown> = {
    ...next,
    // AFTER ...next, so the resolved fallback wins over the blank value still
    // sitting in the form state.
    ...(effectiveDateForLaunch ? { effective_date: effectiveDateForLaunch } : {}),
    // The picked term's OWN dates — the substrate's confirmed-term match key.
    // Safe to send unconditionally: `resolveLaunchTerm` already rejected any
    // submit whose effective_date falls outside this same term.
    ...(chosenTerm?.effective_date
      ? { policy_effective_date: chosenTerm.effective_date }
      : {}),
    ...(chosenTerm?.expiration_date
      ? { policy_expiration_date: chosenTerm.expiration_date }
      : {}),
    ...(injected ?? {}),
  };
  return { inputs, dropped: [] };
}
